import { useState, useEffect, useCallback } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import {
  SidePanel,
  SidePanelType,
  ButtonPrimary,
  IconLoader,
  FooterContentPosition,
  ajvResolver,
  ResponseError,
  responseErrorsResolver,
  PermissionEnum,
  Popup,
  PermissionDenied,
  ButtonSecondary,
  usePrevious,
  TransactionTypeEnum,
  Tabs,
  ChangeLogs,
} from '@ui/src';
import { useAppDispatch, useAppSelector } from '@store';
import { onSubmit, clearSettingStateState } from './store/slices/form';
import TransactionMonitoringRuleForm from '../../components/transaction-monitoring-rules/form';
import {
  TransactionMonitoringRule,
  TransactionMonitoringRuleEntity,
  TransactionMonitoringRuleFormValues,
} from '../types';
import {
  useCreateTransactionMonitoringRuleMutation,
  useLazyGetTransactionMonitoringRuleQuery,
  useUpdateTransactionMonitoringRuleMutation,
  useToggleTransationMonitoringRulesActiveMutation,
  useLazyGetMonitoringRuleChangeLogsQuery,
} from './store/services';

import ROUTES from '../../routes';

import cn from './transaction-monitoring-rules.module.css';

const scheme = {
  type: 'object',
  properties: {
    name: {
      type: 'string',
      minLength: 1,
      errorMessage: 'Rule name is required',
    },
    counterParty: {
      type: 'string',
      minLength: 1,
      errorMessage: 'Counterparty is required',
    },
    periodOfTimeInHours: {
      type: 'string',
      minLength: 1,
      errorMessage: 'Period is required',
    },
    action: {
      type: 'string',
      minLength: 1,
      errorMessage: 'Action is required',
    },
    entity: {
      type: 'string',
      minLength: 1,
      errorMessage: 'Entity is required',
    },
  },
  required: ['periodOfTimeInHours', 'action', 'counterParty', 'name', 'entity'],
};

const TransactionMonitoringRuleFormHOC = () => {
  const [dirtyPopupVisible, setDirtyPopupVisible] = useState(false);
  const [panelIsOpen, setPanelIsOpen] = useState(false);
  const [globalError, setGlobalError] = useState('');
  const [formattedScheme, setFormattedScheme] = useState(scheme);
  const [activeTab, setActiveTab] = useState<'general' | 'logs'>('general');

  const { ruleId } = useParams();

  const {
    transactionMonitoringRules: {
      form: {
        submitSuccess,
        isLoading,
        formValues,
        requestInProgress,
        monitoringRuleLogs,
        monitoringRulePagination,
      },
    },
    amlDictionaries: { activePermissions, paymentTypes },
    administrationDictionaries: { users },
  } = useAppSelector((state) => state);

  const {
    control,
    handleSubmit,
    formState: { isDirty, errors },
    reset,
    setError,
    watch,
    setValue,
    trigger,
    clearErrors,
  } = useForm<TransactionMonitoringRuleFormValues>({
    resolver: ajvResolver(formattedScheme),
  });

  const number = watch('number');
  const volumeFrom = watch('volumeFrom');
  const volumeTo = watch('volumeTo');
  const periodOfTimeInHours = watch('periodOfTimeInHours');
  const entity = watch('entity');
  const transactionType = watch('transactionType');

  const prevTransactionEntity = usePrevious(entity);
  const prevTransactionType = usePrevious(transactionType);

  const { search } = useLocation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const [create] = useCreateTransactionMonitoringRuleMutation();
  const [update] = useUpdateTransactionMonitoringRuleMutation();
  const [getRule] = useLazyGetTransactionMonitoringRuleQuery();
  const [
    getMonitoringRuleLogs,
    { isLoading: changeLogsIsLoading, isFetching: changeLogsIsFetching },
  ] = useLazyGetMonitoringRuleChangeLogsQuery();

  const [updateStatus, { isLoading: updateInProgress }] =
    useToggleTransationMonitoringRulesActiveMutation();

  const getChangeLogsAction = useCallback(
    (cursor?: string) => {
      getMonitoringRuleLogs(
        `?take=20&entityType=MONITORING_RULE&entityId=${ruleId}${
          cursor ? `&cursor=${cursor}` : ''
        }`,
      );
    },
    [ruleId],
  );

  const onFormSubmit = handleSubmit(async (values) => {
    setGlobalError('');

    if (values.name) {
      const body: TransactionMonitoringRuleFormValues = {
        number: Number(values.number),
        volumeFrom: Number(values.volumeFrom),
        volumeTo: Number(values.volumeTo),
        periodOfTimeInHours: Number(values.periodOfTimeInHours),
        clientType: values.clientType,
        counterParty: values.counterParty,
        name: values.name,
        action: values.action,
        entity: values.entity,
      };

      if (values.paymentType) {
        body.paymentType = values.paymentType;
      } else if (!values.paymentType && formValues?.paymentType) {
        body.paymentType = null;
      }

      if (values.transactionType) {
        body.transactionType = values.transactionType;
      }

      if (values.paymentOrderType) {
        body.paymentOrderType = values.paymentOrderType;
      }

      dispatch(onSubmit(body));

      let response;

      if (ruleId) {
        response = (await update({
          body,
          id: ruleId,
        })) as {
          data?: TransactionMonitoringRule;
          error?: ResponseError;
        };
      } else {
        response = (await create(body)) as {
          data?: TransactionMonitoringRule;
          error?: ResponseError;
        };
      }

      if (response.error) {
        responseErrorsResolver({
          errors: response.error,
          setError,
          setGlobalError,
        });
      }
    } else {
      setError('name', {
        type: 'custom',
        message: 'Name is required',
      });
    }
  });

  const getRuleAction = useCallback(() => {
    getRule(ruleId as string);
  }, [ruleId]);

  const handleClose = useCallback(() => {
    setPanelIsOpen(false);

    setTimeout(() => {
      navigate(`${ROUTES.TRANSACTION_MONITORING_RULES}${search || ''}`);
    }, 100);
  }, []);

  useEffect(() => {
    if (submitSuccess) {
      handleClose();
    }
  }, [submitSuccess]);

  useEffect(() => {
    let nextScheme: any = scheme;

    if (entity === TransactionMonitoringRuleEntity.PAYMENT_ORDER) {
      setValue('transactionType', '');
      setValue('paymentType', '');

      nextScheme = {
        ...scheme,
        properties: {
          ...scheme.properties,
          paymentOrderType: {
            type: 'string',
            minLength: 1,
            errorMessage: 'Payment Order type is required',
          },
        },
        required: [...scheme.required, 'paymentOrderType'],
      };
    } else if (
      prevTransactionEntity === TransactionMonitoringRuleEntity.PAYMENT_ORDER &&
      entity === TransactionMonitoringRuleEntity.TRANSACTION
    ) {
      nextScheme = {
        ...scheme,
        properties: {
          ...scheme.properties,
          transactionType: {
            type: 'string',
            minLength: 1,
            errorMessage: 'Transaction type is required',
          },
        },
        required: [...scheme.required, 'transactionType'],
      };

      setValue('paymentOrderType', '');
    }

    setFormattedScheme(nextScheme);
  }, [entity, prevTransactionEntity]);

  useEffect(() => {
    if (
      transactionType === TransactionTypeEnum.INTERNAL &&
      prevTransactionType &&
      (prevTransactionType === TransactionTypeEnum.INCOMING ||
        prevTransactionType.OUTGOING)
    ) {
      setValue('paymentType', '');
    }
  }, [transactionType, prevTransactionType]);

  useEffect(() => {
    setPanelIsOpen(true);

    if (ruleId) {
      getRuleAction();
    }

    return () => {
      dispatch(clearSettingStateState());
    };
  }, [dispatch, ruleId, getRuleAction]);

  useEffect(() => {
    if (formValues && ruleId) {
      reset({
        ...formValues,
        periodOfTimeInHours: formValues.periodOfTimeInHours.toString(),
      });
    }
  }, [reset, formValues, ruleId]);

  const isEditable = activePermissions?.includes(
    PermissionEnum.AML_MONITORING_RULES_EDIT_ACTION,
  );

  let title;

  useEffect(() => {
    if (number) {
      setValue('number', Number(number));
      trigger('number');
    }
  }, [number]);

  useEffect(() => {
    if (volumeFrom) {
      setValue('volumeFrom', Number(volumeFrom));
      trigger('volumeFrom');
    }
  }, [volumeFrom]);

  useEffect(() => {
    if (volumeTo) {
      setValue('volumeTo', Number(volumeTo));
      trigger('volumeTo');
    }
  }, [volumeTo]);

  useEffect(() => {
    if (periodOfTimeInHours) {
      setValue('periodOfTimeInHours', periodOfTimeInHours);
      trigger('periodOfTimeInHours');
    }
  }, [periodOfTimeInHours]);

  useEffect(() => {
    if (
      activeTab === 'logs' &&
      !monitoringRuleLogs &&
      activePermissions?.includes(PermissionEnum.AUDIT_LOG_LIST) &&
      panelIsOpen
    ) {
      getChangeLogsAction();
    }
  }, [activeTab, panelIsOpen]);

  let content = (
    <>
      {ruleId && (
        <Tabs
          tabs={[
            {
              name: 'General',
              value: 'general',
            },
            {
              name: 'Change Logs',
              value: 'logs',
            },
          ]}
          activeTab={activeTab}
          onClick={(tab) => setActiveTab(tab as 'general' | 'logs')}
          wrapperClassName={cn.monitoringRulesTabs}
        />
      )}
      {activeTab === 'general' && (
        <TransactionMonitoringRuleForm
          control={control}
          errors={errors}
          isEditable={isEditable}
          ruleId={ruleId}
          entity={entity as TransactionMonitoringRuleEntity}
          transactionType={transactionType}
          paymentTypes={paymentTypes?.map((item) => ({
            name: item.name,
            value: item.paymentType,
          }))}
          setValue={setValue}
          clearErrors={clearErrors}
        />
      )}
      {activeTab === 'logs' && (
        <div className={cn.monitoringRuleChangeLogs}>
          {activePermissions?.includes(PermissionEnum.AUDIT_LOG_LIST) && (
            <ChangeLogs
              entityType="MONITORING_RULE"
              entityId={ruleId as string}
              changeLogsIsLoading={changeLogsIsLoading}
              changeLogs={monitoringRuleLogs}
              activePermission={activePermissions}
              users={users}
              pagination={monitoringRulePagination}
              changeLogsIsFetching={changeLogsIsFetching}
              getChangeLogs={getChangeLogsAction}
            />
          )}
          {!activePermissions?.includes(PermissionEnum.AUDIT_LOG_LIST) && (
            <PermissionDenied />
          )}
        </div>
      )}
    </>
  );

  if (isLoading) {
    content = (
      <div className={cn.ruleFormLoader}>
        <IconLoader />
      </div>
    );
  } else if (
    !isLoading &&
    !ruleId &&
    !activePermissions?.includes(PermissionEnum.AML_MONITORING_RULES_ADD_ACTION)
  ) {
    content = <PermissionDenied />;
  }

  if (ruleId) {
    title = 'Edit rule';

    if (!isEditable) {
      title = 'Rule info';
    }
  } else {
    title = 'Add new rule';
  }

  let footerButton;

  if (
    (ruleId &&
      activePermissions?.includes(
        PermissionEnum.AML_MONITORING_RULES_EDIT_ACTION,
      )) ||
    (!ruleId &&
      activePermissions?.includes(
        PermissionEnum.AML_MONITORING_RULES_ADD_ACTION,
      ))
  ) {
    footerButton = (
      <ButtonPrimary
        text={requestInProgress ? 'Loading...' : 'Save rule'}
        onClick={onFormSubmit}
        className={cn.ruleSubmit}
        bold
        disabled={!isDirty}
      />
    );
  } else {
    footerButton = (
      <ButtonPrimary
        text="Close"
        onClick={isDirty ? () => setDirtyPopupVisible(true) : handleClose}
        className={cn.ruleSubmit}
        bold
      />
    );
  }

  let statusButtonText;

  if (updateInProgress) {
    statusButtonText = 'Loading...';
  } else {
    statusButtonText = formValues?.active ? 'Deactivate' : 'Activate';
  }

  return (
    <>
      <SidePanel
        type={SidePanelType.AML}
        closeOnClickOutside
        isOpen={panelIsOpen}
        onClose={isDirty ? () => setDirtyPopupVisible(true) : handleClose}
        title={title}
        sidepanelExpandedKey="monitoring-rules"
        footerContentPosition={
          !activePermissions?.includes(
            PermissionEnum.AML_MONITORING_RULES_DELETE_ACTION,
          )
            ? FooterContentPosition.Center
            : FooterContentPosition.Left
        }
        errorSidepanel={globalError}
        footer={
          <>
            {footerButton}
            {!isLoading &&
              ruleId &&
              activePermissions?.includes(
                PermissionEnum.AML_MONITORING_RULES_DELETE_ACTION,
              ) && (
                <ButtonSecondary
                  text={statusButtonText}
                  onClick={() =>
                    updateStatus({
                      id: ruleId,
                      active: !formValues?.active,
                    })
                  }
                />
              )}
          </>
        }
      >
        {content}
      </SidePanel>
      <Popup
        title="You have unsaved changes that will be lost"
        description="Are you sure you want to close this form?"
        onCancelClick={() => setDirtyPopupVisible(false)}
        onOkClick={handleClose}
        cancelButtonText="Cancel"
        okButtonText="Close"
        isVisible={dirtyPopupVisible}
        parent={document.getElementById('dirty-popup') as HTMLElement}
      />
      <div id="dirty-popup" />
    </>
  );
};

export default TransactionMonitoringRuleFormHOC;
